import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Building2,
  Stethoscope,
  Heart,
  Droplets,
  Shield,
  Phone,
  Save,
  Trash2,
  Loader2,
  Plus,
  X,
  AlertTriangle,
  CreditCard,
  Baby,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyInfo } from '../hooks/usePregnancyInfo';
import { useLimits } from '@/hooks/useLimits';
import {
  PregnancyInfoData,
  EmergencyContact,
  BLOOD_TYPES,
  RHESUS_FACTORS,
  RELATIONSHIPS,
} from '../types';

export function PregnancyInfoPage() {
  const { t } = useTranslation('pregnancyInfo');
  const navigate = useNavigate();
  const { data, isLoading, isSaving, error, saveData, deleteData, isBabyMode } = usePregnancyInfo();
  const { isLimitEnabled, isLoading: isLimitsLoading } = useLimits();
  const isPregnancyCardAllowed = isLimitEnabled('pregnancy_card');

  // Title changes based on mode
  const pageTitle = isBabyMode ? t('pageTitle.baby') : t('pageTitle.pregnancy');

  // Formular-State
  const [formData, setFormData] = useState<PregnancyInfoData>({
    hospital: {},
    gynecologist: {},
    midwife: {},
    medical_data: {},
    insurance: {},
    emergency_contacts: [],
    pediatrician: {},
    baby_medical_data: {},
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initial-Daten laden
  useEffect(() => {
    if (data) {
      setFormData({
        hospital: data.hospital || {},
        gynecologist: data.gynecologist || {},
        midwife: data.midwife || {},
        medical_data: data.medical_data || {},
        insurance: data.insurance || {},
        emergency_contacts: data.emergency_contacts || [],
        pediatrician: data.pediatrician || {},
        baby_medical_data: data.baby_medical_data || {},
      });
    }
  }, [data]);

  // Handler für verschachtelte Updates
  const updateField = (
    section: keyof PregnancyInfoData,
    field: string,
    value: string | number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] as Record<string, unknown>),
        [field]: value,
      },
    }));
  };

  // Notfallkontakt hinzufügen
  const addEmergencyContact = () => {
    if ((formData.emergency_contacts?.length || 0) >= 3) return;
    setFormData((prev) => ({
      ...prev,
      emergency_contacts: [
        ...(prev.emergency_contacts || []),
        { name: '', phone: '', relationship: 'Partner/in' },
      ],
    }));
  };

  // Notfallkontakt entfernen
  const removeEmergencyContact = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts?.filter((_, i) => i !== index) || [],
    }));
  };

  // Notfallkontakt aktualisieren
  const updateEmergencyContact = (
    index: number,
    field: keyof EmergencyContact,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts?.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      ) || [],
    }));
  };

  // Speichern
  const handleSave = async () => {
    const success = await saveData(formData);
    if (success) {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  // Löschen
  const handleDelete = async () => {
    const success = await deleteData();
    if (success) {
      navigate('/profile');
    }
  };

  if (isLoading || isLimitsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Show premium upgrade message if feature is not allowed for user's tier
  if (!isPregnancyCardAllowed) {
    return (
      <div className="space-y-6 pb-8">
        <h1 className="text-2xl font-bold text-white">{pageTitle}</h1>
        <div className="glass-effect rounded-[15px] p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-pp-orange" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">
              {t('premium.title')}
            </h2>
            <p className="text-white/60 mb-4">
              {t('premium.description')}
            </p>
            <Link
              to="/subscription"
              className="inline-block py-3 px-6 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors"
            >
              {t('premium.upgrade')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">{pageTitle}</h1>
      </div>

      <p className="text-sm text-white/60">
        {isBabyMode
          ? t('description.baby')
          : t('description.pregnancy')}
      </p>

      {error && (
        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-[15px] text-red-300 text-sm">
          {error}
        </div>
      )}

      {saveSuccess && (
        <div className="p-3 bg-green-500/20 border border-green-500/30 rounded-[15px] text-green-300 text-sm">
          {t('messages.changesSaved')}
        </div>
      )}

      {/* Krankenhaus */}
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-blue-400" />
          <h2 className="font-semibold text-white">{t('sections.hospital')}</h2>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.name')}</label>
            <input
              value={formData.hospital?.name || ''}
              onChange={(e) => updateField('hospital', 'name', e.target.value)}
              placeholder={t('placeholders.hospitalName')}
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.address')}</label>
            <input
              value={formData.hospital?.address || ''}
              onChange={(e) => updateField('hospital', 'address', e.target.value)}
              placeholder={t('placeholders.streetAddress')}
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.phone')}</label>
              <input
                type="tel"
                value={formData.hospital?.phone || ''}
                onChange={(e) => updateField('hospital', 'phone', e.target.value)}
                placeholder={t('placeholders.phoneNumber')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.deliveryRoom')}</label>
              <input
                type="tel"
                value={formData.hospital?.delivery_phone || ''}
                onChange={(e) => updateField('hospital', 'delivery_phone', e.target.value)}
                placeholder={t('placeholders.deliveryRoom')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Frauenarzt */}
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-center gap-2 mb-4">
          <Stethoscope className="w-5 h-5 text-teal-400" />
          <h2 className="font-semibold text-white">{t('sections.gynecologist')}</h2>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.practice')}</label>
            <input
              value={formData.gynecologist?.practice || ''}
              onChange={(e) => updateField('gynecologist', 'practice', e.target.value)}
              placeholder={t('placeholders.practiceName')}
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.phone')}</label>
              <input
                type="tel"
                value={formData.gynecologist?.phone || ''}
                onChange={(e) => updateField('gynecologist', 'phone', e.target.value)}
                placeholder={t('placeholders.phoneNumber')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.emergencyPhone')}</label>
              <input
                type="tel"
                value={formData.gynecologist?.emergency_phone || ''}
                onChange={(e) => updateField('gynecologist', 'emergency_phone', e.target.value)}
                placeholder={t('placeholders.mobileEmergency')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hebamme */}
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-5 h-5 text-pink-400" />
          <h2 className="font-semibold text-white">{t('sections.midwife')}</h2>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.name')}</label>
            <input
              value={formData.midwife?.name || ''}
              onChange={(e) => updateField('midwife', 'name', e.target.value)}
              placeholder={t('placeholders.fullName')}
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
            />
          </div>
          <div className={isBabyMode ? '' : 'grid grid-cols-2 gap-3'}>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.phone')}</label>
              <input
                type="tel"
                value={formData.midwife?.phone || ''}
                onChange={(e) => updateField('midwife', 'phone', e.target.value)}
                placeholder={t('placeholders.phoneNumber')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
            {!isBabyMode && (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('fields.onCallFromWeek')}
                </label>
                <input
                  type="number"
                  min={30}
                  max={42}
                  value={formData.midwife?.on_call_from_ssw || ''}
                  onChange={(e) =>
                    updateField('midwife', 'on_call_from_ssw', parseInt(e.target.value) || 0)
                  }
                  placeholder={t('placeholders.onCallWeek')}
                  className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Kinderarzt - nur im Baby-Modus */}
      {isBabyMode && (
        <div className="glass-effect rounded-[15px] p-4">
          <div className="flex items-center gap-2 mb-4">
            <Baby className="w-5 h-5 text-pp-orange" />
            <h2 className="font-semibold text-white">{t('sections.pediatrician')}</h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.practice')}</label>
              <input
                value={formData.pediatrician?.practice || ''}
                onChange={(e) => updateField('pediatrician', 'practice', e.target.value)}
                placeholder={t('placeholders.pediatricianPractice')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.doctorName')}</label>
              <input
                value={formData.pediatrician?.doctor_name || ''}
                onChange={(e) => updateField('pediatrician', 'doctor_name', e.target.value)}
                placeholder={t('placeholders.doctorName')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.address')}</label>
              <input
                value={formData.pediatrician?.address || ''}
                onChange={(e) => updateField('pediatrician', 'address', e.target.value)}
                placeholder={t('placeholders.streetAddress')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.phone')}</label>
              <input
                type="tel"
                value={formData.pediatrician?.phone || ''}
                onChange={(e) => updateField('pediatrician', 'phone', e.target.value)}
                placeholder={t('placeholders.phoneNumber')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* Medizinische Daten Baby - nur im Baby-Modus */}
      {isBabyMode && (
        <div className="glass-effect rounded-[15px] p-4">
          <div className="flex items-center gap-2 mb-4">
            <Droplets className="w-5 h-5 text-pink-400" />
            <h2 className="font-semibold text-white">{t('sections.medicalDataBaby')}</h2>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.bloodType')}</label>
                <select
                  value={formData.baby_medical_data?.blood_type || ''}
                  onChange={(e) => updateField('baby_medical_data', 'blood_type', e.target.value)}
                  className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                >
                  <option value="" className="bg-pp-dark text-white">{t('placeholders.select')}</option>
                  {BLOOD_TYPES.map((type) => (
                    <option key={type} value={type} className="bg-pp-dark text-white">
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.rhesusFactor')}</label>
                <select
                  value={formData.baby_medical_data?.rhesus_factor || ''}
                  onChange={(e) => updateField('baby_medical_data', 'rhesus_factor', e.target.value)}
                  className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                >
                  <option value="" className="bg-pp-dark text-white">{t('placeholders.select')}</option>
                  {RHESUS_FACTORS.map((factor) => (
                    <option key={factor} value={factor} className="bg-pp-dark text-white">
                      {factor} ({factor === 'positiv' ? 'Rh+' : 'Rh-'})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.allergies')}</label>
              <input
                value={formData.baby_medical_data?.allergies || ''}
                onChange={(e) => updateField('baby_medical_data', 'allergies', e.target.value)}
                placeholder={t('placeholders.allergyExamplesBaby')}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                {t('fields.specialNotes')}
              </label>
              <textarea
                value={formData.baby_medical_data?.notes || ''}
                onChange={(e) => updateField('baby_medical_data', 'notes', e.target.value)}
                placeholder={t('placeholders.medicalNotesBaby')}
                rows={2}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent resize-none"
              />
            </div>
          </div>
        </div>
      )}

      {/* Medizinische Daten */}
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-center gap-2 mb-4">
          <Droplets className="w-5 h-5 text-red-400" />
          <h2 className="font-semibold text-white">{isBabyMode ? t('sections.medicalDataMother') : t('sections.medicalData')}</h2>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.bloodType')}</label>
              <select
                value={formData.medical_data?.blood_type || ''}
                onChange={(e) => updateField('medical_data', 'blood_type', e.target.value)}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              >
                <option value="" className="bg-pp-dark text-white">{t('placeholders.select')}</option>
                {BLOOD_TYPES.map((type) => (
                  <option key={type} value={type} className="bg-pp-dark text-white">
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.rhesusFactor')}</label>
              <select
                value={formData.medical_data?.rhesus_factor || ''}
                onChange={(e) => updateField('medical_data', 'rhesus_factor', e.target.value)}
                className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
              >
                <option value="" className="bg-pp-dark text-white">{t('placeholders.select')}</option>
                {RHESUS_FACTORS.map((factor) => (
                  <option key={factor} value={factor} className="bg-pp-dark text-white">
                    {factor} ({factor === 'positiv' ? 'Rh+' : 'Rh-'})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.allergies')}</label>
            <input
              value={formData.medical_data?.allergies || ''}
              onChange={(e) => updateField('medical_data', 'allergies', e.target.value)}
              placeholder={t('placeholders.allergyExamples')}
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('fields.specialNotes')}
            </label>
            <textarea
              value={formData.medical_data?.notes || ''}
              onChange={(e) => updateField('medical_data', 'notes', e.target.value)}
              placeholder={t('placeholders.medicalNotes')}
              rows={2}
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent resize-none"
            />
          </div>
        </div>
      </div>

      {/* Versicherung */}
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-purple-400" />
          <h2 className="font-semibold text-white">{t('sections.insurance')}</h2>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">{t('fields.insuranceProvider')}</label>
            <input
              value={formData.insurance?.provider || ''}
              onChange={(e) => updateField('insurance', 'provider', e.target.value)}
              placeholder={t('placeholders.insuranceProvider')}
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              {t('fields.policyNumber')}
            </label>
            <input
              value={formData.insurance?.number || ''}
              onChange={(e) => updateField('insurance', 'number', e.target.value)}
              placeholder={t('placeholders.insuranceCard')}
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Notfallkontakte */}
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-amber-400" />
            <h2 className="font-semibold text-white">{t('sections.emergencyContacts')}</h2>
          </div>
          {(formData.emergency_contacts?.length || 0) < 3 && (
            <button
              onClick={addEmergencyContact}
              className="text-sm text-pp-orange hover:text-pp-orange/80 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              {t('actions.add')}
            </button>
          )}
        </div>

        {formData.emergency_contacts?.length === 0 ? (
          <p className="text-sm text-white/50 text-center py-4">
            {t('messages.noEmergencyContacts')}
          </p>
        ) : (
          <div className="space-y-4">
            {formData.emergency_contacts?.map((contact, index) => (
              <div key={index} className="p-3 bg-white/10 rounded-[10px] relative">
                <button
                  onClick={() => removeEmergencyContact(index)}
                  className="absolute top-2 right-2 p-1 text-white/40 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="space-y-2 pr-6">
                  <div>
                    <label className="block text-xs font-medium text-white/50 mb-1">{t('fields.contactName')}</label>
                    <input
                      value={contact.name}
                      onChange={(e) => updateEmergencyContact(index, 'name', e.target.value)}
                      placeholder={t('placeholders.fullName')}
                      className="w-full px-4 py-2.5 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-white/50 mb-1">
                        {t('fields.phone')}
                      </label>
                      <input
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => updateEmergencyContact(index, 'phone', e.target.value)}
                        placeholder={t('placeholders.phoneNumber')}
                        className="w-full px-4 py-2.5 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-white/50 mb-1">
                        {t('fields.relationship')}
                      </label>
                      <select
                        value={contact.relationship}
                        onChange={(e) =>
                          updateEmergencyContact(index, 'relationship', e.target.value)
                        }
                        className="w-full px-4 py-2.5 rounded-[10px] bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                      >
                        {RELATIONSHIPS.map((rel) => (
                          <option key={rel} value={rel} className="bg-pp-dark text-white">
                            {rel}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aktionen */}
      <div className="flex flex-col gap-3 pt-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full py-4 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('actions.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {t('actions.saveChanges')}
            </>
          )}
        </button>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full py-3 glass-effect border border-red-500/30 text-red-400 font-medium rounded-[15px] hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          {t('actions.deleteAll')}
        </button>
      </div>

      {/* Lösch-Bestätigung */}
      {showDeleteConfirm && (
        <>
          <div
            className="fixed inset-0 bg-pp-dark/80 backdrop-blur-sm z-[9999]"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-[9999] flex items-end sm:items-center justify-center sm:inset-0 sm:p-4">
            <div className="bg-pp-dark/35 backdrop-blur-md border-t border-white/25 rounded-t-2xl sm:rounded-[15px] w-full sm:max-w-sm p-6 animate-in slide-in-from-bottom sm:zoom-in-95 duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-500/20 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="font-semibold text-white">{t('deleteConfirm.title')}</h3>
              </div>
              <p className="text-sm text-white/70 mb-6">
                {t('deleteConfirm.description')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-3 glass-effect border border-white/25 text-white font-medium rounded-[15px] hover:bg-white/10 transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isSaving}
                  className="flex-1 py-3 bg-red-500 text-white font-medium rounded-[15px] hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {isSaving ? t('actions.deleting') : t('actions.delete')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PregnancyInfoPage;
